import {
    CheckboxControl,
    FormTokenField,
    Icon,
    TextControl,
    Tooltip
} from '@wordpress/components'
import {useDispatch, useSelect} from '@wordpress/data'
import {PluginDocumentSettingPanel} from '@wordpress/edit-post'
import {__} from '@wordpress/i18n'
import {useEffect, useState} from "@wordpress/element"
import apiFetch from "@wordpress/api-fetch"
import icons from "./Icons"


const MetaBox = () => {
    const [isSendingNotification, setIsSendingNotification] = useState(false)
    const [isCustomizingContent, setIsCustomizingContent] = useState(false)
    const [isCustomizingAudience, setIsCustomizingAudience] = useState(false)
    const [customTitle, setCustomTitle] = useState()
    const [customBody, setCustomBody] = useState()
    const [selectedSegmentIds, setSelectedSegmentIds] = useState([])
    const [segments, setSegments] = useState([])

    const {postMeta} = useSelect((select) => {
        return {
            postMeta: select('core/editor').getEditedPostAttribute('meta'),
        }
    })

    const {editPost} = useDispatch('core/editor', [
        postMeta?.pushly_unique,
        postMeta?.pushly_send_notification,
        postMeta?.pushly_customize_notification_content,
        postMeta?.pushly_custom_title,
        postMeta?.pushly_custom_body,
        postMeta?.pushly_customize_audience,
        postMeta?.pushly_audience_ids,
    ])

    // load segments
    useEffect(() => {
        apiFetch({path: '/pushly/v1/segments'}).then((segments) => {
            if (segments?.status === 'success') {
                setSegments(segments.data)
            }
        })
    }, [])


    // set the initial post meta values
    useEffect(() => {
        setIsSendingNotification(postMeta?.pushly_send_notification)
        setIsCustomizingContent(postMeta?.pushly_customize_notification_content)
        setCustomTitle(postMeta?.pushly_custom_title)
        setCustomBody(postMeta?.pushly_custom_body)
        setIsCustomizingAudience(postMeta?.pushly_customize_audience)
        setSelectedSegmentIds(postMeta?.pushly_audience_ids)

        /**
         * The editor won't send the post meta when a post is initially saved if none of the meta values have
         * changed from what was initially returned from the backend. In order to work around this issue we
         * set this erroneous pushly_unique meta value on page load to ensure the post meta is always sent.
         *
         * The primary issue we're working around here is that we want `pushly_send_notification` to:
         *   1. On a new post default to the auto_send_status
         *   2. On editing an existing post default to the previously saved status
         *
         * Because `pushly_send_notification` is a boolean and cannot be set to `null` we must first set it
         * to the boolean value matching `auto_send_enabled` - In the case of auto_send_enabled=true the
         * initial page load will show the box checked but the checked value is not actually sent to the
         * backend on post save (because the form thinks nothing in the `meta` has changed). As a result
         * of this, the backend will not see the `pushly_send_notification` was enabled so no notification
         * will be sent.
         */
        editPost({meta: {pushly_unique: Date.now()}})
    }, [])

    return (
        <PluginDocumentSettingPanel
            name="pushly-meta-box"
            title={__('Pushly Notifications', 'pushly')}
            icon={icons.pushly}
            __nextHasNoMarginBottom
        >
            <div id="pushly-meta-box">

                <CheckboxControl
                    checked={isSendingNotification}
                    // help={__('Schedule a notification for this post', 'pushly')}
                    label={__('Send Notification', 'pushly')}
                    onChange={(value) => {
                        setIsSendingNotification(value)
                        editPost({
                            meta: {
                                pushly_send_notification: value
                            }
                        })
                    }
                    }
                    __nextHasNoMarginBottom
                />
                <CheckboxControl
                    checked={isCustomizingContent}
                    disabled={!isSendingNotification}
                    // help={__('Schedule a notification for this post', 'pushly')}
                    label={__('Customize Content', 'pushly')}
                    onChange={(value) => {
                        setIsCustomizingContent(value)
                        editPost({
                            meta: {
                                pushly_customize_notification_content: value
                            }
                        })
                    }}
                    __nextHasNoMarginBottom
                />
                {isCustomizingContent && (
                    <>
                        <TextControl
                            name="pushlyCustomTitle"
                            label={__('Title')}
                            value={customTitle}
                            onChange={(value) => {
                                setCustomTitle(value)
                                editPost({
                                    meta: {
                                        pushly_custom_title: value
                                    }
                                })
                            }}
                            __next40pxDefaultSize
                            __nextHasNoMarginBottom
                        />
                        <TextControl
                            name="pushlyCustomBody"
                            label={__('Body')}
                            value={customBody}
                            onChange={(value) => {
                                setCustomBody(value)
                                editPost({
                                    meta: {
                                        pushly_custom_body: value
                                    }
                                })
                            }}
                            __next40pxDefaultSize
                            __nextHasNoMarginBottom
                        />
                    </>
                )}
                {segments.length > 0 && (
                    <div className='pushly-row'>
                        <CheckboxControl
                            checked={isCustomizingAudience}
                            disabled={!isSendingNotification}
                            label={__('Refine Audience', 'pushly')}
                            onChange={(value) => {
                                setIsCustomizingAudience(value)
                                editPost({
                                    meta: {
                                        pushly_customize_audience: value
                                    }
                                })
                            }}
                            __nextHasNoMarginBottom
                        />
                        <Tooltip
                            text={__('When checked the notification will be targeted to specific users ' +
                                'When unchecked all users will be targeted.', 'pushly')}
                        >
                            <Icon icon='info-outline' size='16'/>
                        </Tooltip>
                    </div>
                )}
                {isCustomizingAudience && segments.length > 0 && (
                    <FormTokenField
                        label={'Targeted Segments'}
                        value={selectedSegmentIds && selectedSegmentIds.flatMap((segmentId) => {
                            const found = segments.find((segment) => segment.id === segmentId)
                            return found ? found.name : []
                        })}
                        suggestions={segments.map(value => value.name)}
                        onChange={(segmentNames) => {
                            const segmentIds = segmentNames
                                .flatMap((segmentName) => {
                                    const found = segments.find((segment) => segment.name === segmentName)
                                    return found ? found.id : []
                                })

                            setSelectedSegmentIds(segmentIds)
                            editPost({
                                meta: {
                                    pushly_audience_ids: segmentIds
                                }
                            })
                        }}
                        __experimentalExpandOnFocus
                        __nextHasNoMarginBottom
                    />
                )}
            </div>
        </PluginDocumentSettingPanel>
    )

}


export default MetaBox
